import * as React from 'react';

import { useUndoCancellationDataForXdp } from 'bundles/subscriptions/utils/cancellationUndoUtils';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import ConsumerSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerSpecializationEnrollButtonNotConfigurable';
import HeroCoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/modules/hero/HeroCoBrandingPartnerLogos';
import HeroCourseraPlusOrLiteCta from 'bundles/unified-description-page-common/components/modules/hero/HeroCourseraPlusOrLiteCta';
import HeroImage from 'bundles/unified-description-page-common/components/modules/hero/HeroImage';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModuleWithImage from 'bundles/unified-description-page-common/components/modules/hero/HeroModuleWithImage';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import HeroSpecializationDescription from 'bundles/unified-description-page-common/components/modules/hero/specialization/HeroSpecializationDescription';
import KeyInformationSpecializationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationSpecializationHorizontalModule';
import ProductOverviewDescription from 'bundles/unified-description-page-common/components/modules/product-overview/Description';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import Markdown from 'bundles/unified-description-page-common/components/shared/Markdown';
import {
  HERO_BACKGROUND_COLOR,
  MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS,
} from 'bundles/unified-description-page-common/constants';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import {
  ProductDetailItemNames,
  type ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import { getConsumerSpecializationKeyInformationItems } from 'bundles/unified-description-page-common/utils/getConsumerKeyInformationItems';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForSpecialization from 'bundles/unified-description-page-common/utils/getPartnersForSpecialization';
import getProductDetailItems from 'bundles/unified-description-page-common/utils/getProductDetailItems';
import getSpecializationDescription from 'bundles/unified-description-page-common/utils/getSpecializationDescription';

type Props = {
  pageData: ConsumerSpecializationPageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

const ProductOverviewModuleRenderer = ({ pageData, config }: Props) => {
  const productTitle = getProductTitle(pageData.name, pageData.productVariant, pageData.slug);
  const partnersToDisplay = getPartnersForSpecialization(pageData.partners, !!pageData.coBrandingEnabled);
  const { navigationLinks } = usePageNavigation();
  const courseraPlusOwnership = useCourseraPlusOwnership();
  const { showPlusMonthlyToAnnualUpgradeButton, isCourseraPlusEligibleProduct } = courseraPlusOwnership ?? {};
  const { showUndoCancellationCTA } = useUndoCancellationDataForXdp({
    productItemId: pageData.id,
    isCourseraPlusEligibleProduct,
    partnerIds: pageData?.partners?.map(({ id }) => id),
  });
  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();
  const showEnrollmentCountOnReviewsItem = showPlusMonthlyToAnnualUpgradeButton || showUndoCancellationCTA;
  const descriptionWithProjectOverview = getSpecializationDescription(
    pageData.cmlDescription,
    pageData.projectsOverview
  );
  const overviewSkills = (
    <Skills
      skills={pageData.skills && pageData.skills.length > 0 ? pageData.skills : pageData.specializationAndCourseSkills}
      disableCTA={config?.removeLinksAboveFold}
    />
  );

  const overviewDescription = pageData.cmlLearningObjectives ? (
    <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} />
  ) : (
    <ProductOverviewDescription
      description={pageData.description}
      cmlDescription={descriptionWithProjectOverview}
      productId={pageData.id}
    />
  );

  const { items: keyInformationItems, shouldRenderStackabilityInDetailsSection } =
    getConsumerSpecializationKeyInformationItems(
      pageData,
      {
        ...config?.keyInfoModule,
      },
      config?.removeLinksAboveFold,
      MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS,
      showEnrollmentCountOnReviewsItem,
      shouldShowXdpChanges
    );

  const getDetails = () => {
    let productDetailItems = config?.productDetailItems;

    if (productDetailItems && shouldRenderStackabilityInDetailsSection) {
      productDetailItems = [...productDetailItems, ProductDetailItemNames.Stackability];

      return <Details detailItems={getProductDetailItems(pageData, productDetailItems)} />;
    }

    return <Details detailItems={getProductDetailItems(pageData, productDetailItems || [])} />;
  };

  let description = config?.heroModule?.shortDescription ? (
    <Markdown source={config.heroModule.shortDescription} />
  ) : (
    <HeroSpecializationDescription headline={pageData.headline} subheader={pageData.subheader} />
  );

  const getAdditionalInfo = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return <HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />;
  };

  const getAdditionalCta = (size?: 'small') => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }
    return (
      courseraPlusOwnership?.isUserEligibleToSeeCta && (
        <HeroCourseraPlusOrLiteCta
          disableCTA={config?.removeLinksAboveFold}
          isSubscribedToCPlusOrLite={courseraPlusOwnership.isSubscribedToCPlusOrLite}
          isSubscribedToCourseraLite={courseraPlusOwnership.isSubscribedToCourseraLite}
          size={size}
        />
      )
    );
  };

  if (config?.showHeroLanguageInfo) {
    description = (
      <>
        {description}
        <HeroLanguageInfo
          languages={{
            primaryLanguages: pageData.primaryLanguages,
            subtitleLanguages: pageData.subtitleLanguages ?? [],
            translatedLanguages: pageData.translatedLanguages ?? [],
            dubbedLanguages: pageData.dubbedLanguages ?? [],
          }}
        />
      </>
    );
  }

  return (
    <ProductOverviewHorizontalModule
      anchorId={SPECIALIZATION_NAV_ITEMS.ABOUT}
      heroBackgroundColor={config?.heroModule?.backgroundColor ?? HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModuleWithImage
          productTitle={productTitle}
          showBackgroundImage={!!config?.showBackgroundImage}
          backgroundImage={config?.backgroundImage}
          partnerLogo={
            partnersToDisplay?.length === 2 ? (
              <HeroCoBrandingPartnerLogos
                // @ts-expect-error URL: string
                leftImage={partnersToDisplay[0].primaryLogo}
                leftPartnerName={partnersToDisplay[0].name}
                // @ts-expect-error URL: string
                rightImage={partnersToDisplay[1].primaryLogo}
                rightPartnerName={partnersToDisplay[1].name}
              />
            ) : (
              // @ts-expect-error URL: string
              <HeroPartnerLogo image={partnersToDisplay?.[0].primaryLogo} partnerName={partnersToDisplay?.[0].name} />
            )
          }
          description={description}
          instructors={
            <HeroInstructors
              slug={pageData.slug}
              instructors={pageData.instructors}
              partners={pageData.partners}
              disableCTA={config?.removeLinksAboveFold}
              displayTopInstructorBadgeInline
            />
          }
          additionalCta={getAdditionalCta('small')}
          primaryCta={
            <ConsumerSpecializationEnrollButtonNotConfigurable
              pageData={pageData}
              onlyShowButton={config?.showAssignButton || showEnrollmentCountOnReviewsItem}
            />
          }
          secondaryCta={
            config?.showAssignButton && (
              <AssignButtonNotConfigurable
                productSlug={pageData.slug}
                productType={
                  pageData.productVariant === 'PROFESSIONAL_CERTIFICATE' ? 'professional-certificate' : 'specialization'
                }
              />
            )
          }
          additionalInfo={getAdditionalInfo()}
          secondaryInfo={!config?.showBackgroundImage && <HeroImage />}
        />
      }
      keyInformation={
        <KeyInformationSpecializationHorizontalModule
          keyInformationItems={keyInformationItems}
          applyLightShadow={!!config?.heroModule?.backgroundColor}
        />
      }
      aboutSubmodule={
        <AboutSubmoduleRenderer
          skills={overviewSkills}
          description={!config?.hideOverviewDescription ? overviewDescription : undefined}
          details={getDetails()}
          config={config}
        />
      }
    />
  );
};

export default ProductOverviewModuleRenderer;
